import type { FC } from 'react';
import { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import Modal from '@wix/da-ds/pkg/modals/dsPresets/Modal';
import BrandedButton from '@wix/da-ds/pkg/Buttons/BrandedButton';
import TextButton from '@wix/da-ds/pkg/Buttons/TextButton';
import WithdrawalCalculator from '../../_partials/WithdrawalCalculator';
import StripePaymentTerms from '../../_partials/StripePaymentTerms';
import WithdrawalErrorBlock from '../../_partials/WithdrawalErrorBlock';
import { DASHBOARD_EXPRESS_URL } from '../consts';
import { makeXHRCall } from '@wix/da-shared-react/pkg/simpleXHR/actionCreators';
import { getIsLoading } from '@wix/da-shared-react/pkg/simpleXHR/selectors';
import {
  SimpleXHREndpoint,
  SimpleXHRMethod,
} from '@wix/da-shared-react/pkg/simpleXHR/constants';
import { PapiRequestStudioEarningsWithdraw } from '@wix/da-papi-types';
import { getStripeFee, getStripeInfo } from '../../../../../selectors/earnings';
import { formatFeeString } from '../../_partials/formatFeeString';
import { useBiLogger } from '@wix/da-bi/pkg/hooks/useBiLogger';
import {
  PureBiData,
  StudioEarningsWithdrawalClickBiEvent,
  StudioWithdrawalPaymentMethod,
} from '@wix/da-bi/pkg/events';

import s from './StripeWithdrawAmountModal.scss';

export interface Props {
  isOpen: boolean;
  requestClose: () => void;
  availableBalance: number;
  minimumWithdrawalAmount?: number;
  onContinue?: () => void;
}

const StripeWithdrawAmountModal: FC<Props> = ({
  isOpen,
  requestClose,
  availableBalance,
  minimumWithdrawalAmount = 0,
  onContinue,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const logEvent = useBiLogger();
  const isLoading = useSelector((state: any) =>
    getIsLoading(state, SimpleXHREndpoint.WITHDRAW_EARNINGS)
  );
  const stripeFee = useSelector(getStripeFee);
  const stripeInfo = useSelector(getStripeInfo);
  const isNonUS = stripeInfo?.country !== 'usa';
  const [amountStr, setAmountStr] = useState<string>('');
  const [errorMessage, setErrorMessage] = useState<string>('');

  const formattedFeeString = formatFeeString(stripeFee);

  const amountNumber = (() => {
    const parsed = parseFloat(amountStr);
    if (Number.isNaN(parsed)) return 0;
    return Math.max(0, Math.min(parsed, availableBalance));
  })();

  const isAmountTooHigh = (() => {
    const parsed = parseFloat(amountStr);
    return !Number.isNaN(parsed) && parsed > availableBalance;
  })();

  const isAmountTooLow = (() => {
    const parsed = parseFloat(amountStr);
    return !Number.isNaN(parsed) && parsed < minimumWithdrawalAmount;
  })();

  const clearError = useCallback(() => {
    if (errorMessage) {
      setErrorMessage('');
    }
  }, [errorMessage]);

  const handleAmountChange = useCallback(
    (value: string) => {
      setAmountStr(value);
      clearError();
    },
    [clearError]
  );

  const calculateFee = useCallback(
    (amount: number) => {
      if (!stripeFee) {
        // Fallback to default if fee not available
        return 2.25 + amount * 0.0025;
      }
      const flatInDollars = parseFloat(stripeFee.flat) / 100; // Convert cents to dollars
      const relativeAsDecimal = parseFloat(stripeFee.relative) / 10000; // Convert basis points to decimal (25 -> 0.0025)
      return flatInDollars + amount * relativeAsDecimal;
    },
    [stripeFee]
  );

  // Reset amount when modal closes and clear error when modal opens
  useEffect(() => {
    if (!isOpen) {
      setAmountStr('');
      setErrorMessage('');
    } else if (isOpen) {
      setErrorMessage('');
    }
  }, [isOpen]);

  const handleWithdraw = useCallback(() => {
    logEvent(
      PureBiData<StudioEarningsWithdrawalClickBiEvent>({
        evid: 159,
        payment_method: StudioWithdrawalPaymentMethod.Stripe,
        process_step: 'withdraw',
      })
    );
    dispatch(
      makeXHRCall({
        papiBase: 'studio',
        endpoint: SimpleXHREndpoint.WITHDRAW_EARNINGS,
        method: SimpleXHRMethod.POST,
        data: {
          method: 'stripe',
          amount: amountNumber.toString(),
        } as PapiRequestStudioEarningsWithdraw,
        onSuccess: () => {
          onContinue?.();
        },
        onError: (response: any) => {
          setErrorMessage(
            response?.errorDescription ||
              t('studio.earnings.stripe.withdraw.error.message')
          );
        },
      })
    );
  }, [dispatch, amountNumber, onContinue, t]);

  return (
    <Modal
      className={s['wrapper']}
      contentClassName={s['content']}
      heading={t('studio.earnings.stripe.withdraw.modal.title')}
      onRequestClose={requestClose}
      showHeaderBorder
      showFooterBorder
      shouldCloseOnOverlayClick
      isOpen={isOpen}
      withScrollableContentWrapper
      footerRight={[
        <TextButton
          onClick={requestClose}
          size="large"
          variant="main"
          key="cancel"
        >
          {t('common.cancel')}
        </TextButton>,
        <BrandedButton
          onClick={handleWithdraw}
          size="large"
          variant="brand-green"
          key="continue"
          disabled={amountNumber <= 0 || isAmountTooHigh || isAmountTooLow}
          loading={isLoading}
        >
          {t('studio.earnings.stripe.withdraw.modal.buttons.withdraw')}
        </BrandedButton>,
      ]}
    >
      <WithdrawalErrorBlock message={errorMessage} />

      <div className={s['subtitle']}>
        {t('studio.earnings.stripe.withdraw.modal.question')}
      </div>

      <div className={s['note']}>
        {t(
          'studio.earnings.stripe.withdraw.modal.description.fee.extendedDynamic',
          {
            fee: formattedFeeString,
            localCurrencyInfo: isNonUS
              ? t(
                  'studio.earnings.stripe.withdraw.modal.description.fee.localCurrencyInfo'
                )
              : '',
          }
        )}{' '}
        <a
          href={t('studio.earnings.feesKBURL')}
          className="text-link-level-2-primary"
        >
          {t('studio.earnings.stripe.withdraw.modal.description.readMore')}
        </a>
      </div>

      <WithdrawalCalculator
        autoFocus
        className={s['calculator-section']}
        withdrawAmount={amountStr}
        onAmountChange={handleAmountChange}
        calculateFee={calculateFee}
        availableBalance={availableBalance}
        minimumAmount={minimumWithdrawalAmount}
        isValidAmount={!isAmountTooHigh && !isAmountTooLow}
      />

      <div className={s['dashboard-section']}>
        <p className={s['dashboard-text']}>
          {t('studio.earnings.stripe.withdraw.modal.dashboard.prefix')}{' '}
          <a
            href={DASHBOARD_EXPRESS_URL}
            className="text-link-level-2-primary"
            target="_blank"
            rel="noopener noreferrer"
          >
            {t('studio.earnings.stripe.withdraw.modal.dashboard.link')}
          </a>{' '}
          {t('studio.earnings.stripe.withdraw.modal.dashboard.suffix')}
        </p>
      </div>

      <StripePaymentTerms className={s['terms-block']} />
    </Modal>
  );
};

StripeWithdrawAmountModal.displayName = 'StripeWithdrawAmountModal';

export default StripeWithdrawAmountModal;
